var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { canonicalize_slates_js, check_election_slates_js } from "sequent-core";
import { EMobileCandidateLists, SLATES_ANNOTATION } from "@sequentech/ui-core";
/** The election form field holding the slate configuration as editable text. */
export var SLATES_FORM_FIELD = "slates_configuration";
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var parseObject = function (text) {
    try {
        var parsed = JSON.parse(text);
        return isRecord(parsed) ? parsed : undefined;
    }
    catch (_a) {
        return undefined;
    }
};
var isProblemList = function (value) {
    return Array.isArray(value) &&
        value.every(function (item) {
            return isRecord(item) &&
                typeof item.severity === "string" &&
                typeof item.code === "string" &&
                typeof item.path === "string" &&
                typeof item.message === "string";
        });
};
/** The stored slate configuration as text an administrator can edit. */
export var readSlatesConfiguration = function (annotations) {
    var stored = annotations === null || annotations === void 0 ? void 0 : annotations[SLATES_ANNOTATION];
    if (typeof stored !== "string") {
        return "";
    }
    var parsed = parseObject(stored);
    return parsed ? JSON.stringify(parsed, null, 2) : stored;
};
/**
 * The annotations to save for the edited slate configuration: its canonical
 * form, or no slate annotation at all when the field was cleared. Every other
 * annotation is kept as it is.
 */
export var writeSlatesConfiguration = function (annotations, text) {
    var result = __assign({}, (annotations !== null && annotations !== void 0 ? annotations : {}));
    if (!(text === null || text === void 0 ? void 0 : text.trim())) {
        delete result[SLATES_ANNOTATION];
        return result;
    }
    result[SLATES_ANNOTATION] = canonicalize_slates_js(text);
    return result;
};
/** The mobile candidate lists default the configuration text sets, if readable. */
export var getMobileCandidateLists = function (text) {
    var parsed = text ? parseObject(text) : undefined;
    if (!parsed) {
        return undefined;
    }
    var value = parsed.mobile_candidate_lists;
    if (value === undefined) {
        return EMobileCandidateLists.COLLAPSED;
    }
    return Object.values(EMobileCandidateLists).find(function (option) { return option === value; });
};
/** The configuration text with its mobile candidate lists default changed. */
export var setMobileCandidateLists = function (text, value) {
    var parsed = parseObject(text);
    if (!parsed) {
        return text;
    }
    var version = parsed.version, _previous = parsed.mobile_candidate_lists, rest = __rest(parsed, ["version", "mobile_candidate_lists"]);
    return JSON.stringify(__assign({ version: version, mobile_candidate_lists: value }, rest), null, 2);
};
/**
 * Everything that would stop the slate configuration from being published,
 * as the server will check it.
 */
export var checkSlatesConfiguration = function (text, defaultLanguage, contests, candidates) {
    var problems = check_election_slates_js(text, defaultLanguage, JSON.stringify(contests), JSON.stringify(candidates));
    if (!isProblemList(problems)) {
        throw new Error("Unexpected answer checking the slate configuration");
    }
    return problems;
};
/** The problems a thrown slate check carries, when that is what was thrown. */
export var slatesProblemsFromError = function (error) {
    return isProblemList(error) ? error : undefined;
};
/**
 * The problems that can be found without the election's contests and
 * candidates. The server still checks the rest before anything is published.
 */
export var checkSlatesStructure = function (text) {
    try {
        canonicalize_slates_js(text);
        return [];
    }
    catch (error) {
        var problems = slatesProblemsFromError(error);
        if (!problems) {
            throw error;
        }
        return problems;
    }
};
export var formatSlatesProblems = function (problems) {
    return problems.map(function (problem) { return "".concat(problem.path, ": ").concat(problem.message); }).join("\n");
};
